import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Facebook, Mail, Globe } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Logo } from '@/components/brand/Logo';
import { WhatsAppGlyph } from '@/components/site/WhatsAppButton';
import {
  AGENCY,
  CONSULTATION_PATH,
  CREDIT,
  NAV_LINKS,
  whatsappLink,
} from '@/lib/site';
import { useI18n } from '@/lib/i18n';

const TikTokGlyph = ({ className = 'h-4 w-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.1v12.4a2.6 2.6 0 1 1-1.86-2.49V9.75a5.7 5.7 0 1 0 4.96 5.65V8.9a7.32 7.32 0 0 0 4.25 1.36V7.16a4.28 4.28 0 0 1-3.19-1.34Z" />
  </svg>
);

const iconButton =
  'inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--rmh-border-strong)] bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)] transition-colors duration-300 hover:border-[color:var(--rmh-ink)]';

export const Footer = () => {
  const { t } = useI18n();
  const waHref = whatsappLink(t.whatsappMessage);

  const socials = [
    {
      name: 'Instagram',
      url: AGENCY.instagram,
      Icon: Instagram,
      testId: 'footer-social-instagram-link',
    },
    {
      name: 'TikTok',
      url: AGENCY.tiktok,
      Icon: TikTokGlyph,
      testId: 'footer-social-tiktok-link',
    },
    {
      name: 'Facebook',
      url: AGENCY.facebook,
      Icon: Facebook,
      testId: 'footer-social-facebook-link',
    },
    {
      name: 'LinkedIn',
      url: AGENCY.linkedin,
      Icon: Linkedin,
      testId: 'footer-social-linkedin-link',
    },
    {
      name: 'WhatsApp',
      url: waHref,
      Icon: WhatsAppGlyph,
      testId: 'footer-social-whatsapp-link',
    },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden rmh-grain border-t border-[color:var(--rmh-border)]"
      style={{ backgroundColor: 'var(--rmh-canvas-2)' }}
    >
      <Container wide className="relative z-10 py-14 lg:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-5">
            <Logo variant="stacked" asLink={false} />
            <p className="rmh-body mt-6 max-w-sm text-[0.9rem]">{t.footer.tagline}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              {socials.map(({ name, url, Icon, testId }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  title={name}
                  data-testid={testId}
                  className={iconButton}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="rmh-eyebrow">{t.footer.navigate}</p>
            <ul className="mt-5 flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-nav-${link.key}-link`}
                    className="rmh-link-underline text-[0.88rem] text-[color:var(--rmh-ink-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
                  >
                    {t.nav[link.key]}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={CONSULTATION_PATH}
                  data-testid="footer-nav-book-link"
                  className="rmh-link-underline text-[0.88rem] text-[color:var(--rmh-ink-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
                >
                  {t.common.bookConsultation}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="rmh-eyebrow">{t.footer.servicesHeading}</p>
            <ul className="mt-5 flex flex-col gap-3">
              {t.services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services#${s.slug}`}
                    data-testid={`footer-service-${s.slug}-link`}
                    className="rmh-link-underline text-[0.88rem] text-[color:var(--rmh-ink-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="rmh-eyebrow mt-9">{t.footer.contactHeading}</p>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href={`mailto:${AGENCY.email}`}
                data-testid="site-footer-email-link"
                className="inline-flex items-center gap-2 text-[0.88rem] text-[color:var(--rmh-ink-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
              >
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {AGENCY.email}
              </a>
              <a
                href={AGENCY.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="site-footer-website-link"
                className="inline-flex items-center gap-2 text-[0.88rem] text-[color:var(--rmh-ink-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
              >
                <Globe className="h-3.5 w-3.5 shrink-0" />
                {AGENCY.website}
              </a>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="site-footer-whatsapp-number-link"
                className="inline-flex items-center gap-2 text-[0.88rem] text-[color:var(--rmh-ink-2)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
              >
                <WhatsAppGlyph className="h-3.5 w-3.5 shrink-0" />
                {AGENCY.whatsappNumber}
              </a>
            </div>
          </div>
        </div>

        <div className="rmh-rule mt-14" />
        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[0.75rem] text-[color:var(--rmh-muted)]">
            &copy; {new Date().getFullYear()} {AGENCY.name}. {t.footer.rights}
          </p>

          <div className="flex items-center gap-2.5">
            <span className="text-[0.75rem] text-[color:var(--rmh-muted)]">
              {t.footer.madeBy}{' '}
              <a
                href={CREDIT.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-credit-link"
                className="rmh-link-underline font-medium text-[color:var(--rmh-ink)]"
              >
                {CREDIT.label}
              </a>
            </span>
            <a
              href={CREDIT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${CREDIT.label} ${t.footer.onInstagram}`}
              title={`${CREDIT.label} ${t.footer.onInstagram}`}
              data-testid="footer-credit-instagram-link"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[color:var(--rmh-border-strong)] bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)] transition-colors duration-300 hover:border-[color:var(--rmh-ink)]"
            >
              <Instagram className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
